import type { ReactElement, ReactNode } from 'react';
import { Badge, Table, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui';
import { TextLink } from '@/components/ui/TextLink';
import { ChevronIcon } from '@/components/catalog/icons';
import { cx } from '@/lib/cx';
import type {
  ProductInsightsCompanyRef,
  ProductInsightsEvent,
  ProductInsightsEventFormat,
} from '@/lib/types';
import { formatCount } from '../use-fab-data';
import {
  FORMAT_LABEL,
  rankedSchools,
  schoolDisplayName,
  schoolGroup,
  splitIntoGroups,
  type SortDir,
} from './events-model';

/**
 * Small presentation pieces shared by the Events and By school tables: the
 * format badge, sortable header button, card label/value pair, the school
 * name cell (company link when it resolved to an EMERGE company) and the
 * inline "who took part" detail for one expanded event row.
 *
 * `SortButton` is kept for callers that have not moved to `PiSortHeader`.
 *
 * ⚠️ The expanded panel heads its reach column "Shared reach" on a virtual
 * event: every school on it reaches the same registrants.
 */

export function FormatBadge({ format }: { format: ProductInsightsEventFormat | null }): ReactElement {
  if (format === null) return <span className="text-fg-muted">—</span>;
  return (
    <Badge tone={format === 'virtual' ? 'info' : 'neutral'}>{FORMAT_LABEL[format]}</Badge>
  );
}

function SortChevron({ state }: { state: SortDir | null }) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-flex shrink-0 transition duration-150 ease-out motion-reduce:transition-none',
        state === null
          ? 'opacity-0 group-hover/sort:opacity-50 group-focus-visible/sort:opacity-50'
          : 'text-fg opacity-100',
      )}
    >
      <ChevronIcon direction={state === 'asc' ? 'up' : 'down'} />
    </span>
  );
}

export function SortButton({
  label,
  state,
  align,
  onClick,
}: {
  label: ReactNode;
  state: SortDir | null;
  align: 'left' | 'right';
  onClick: () => void;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'group/sort inline-flex items-center gap-1 rounded-sm font-medium transition-colors duration-150 ease-out hover:text-fg motion-reduce:transition-none',
        align === 'right' && 'flex-row-reverse',
        state !== null && 'text-fg',
      )}
    >
      <span>{label}</span>
      <SortChevron state={state} />
    </button>
  );
}

export function CardPair({ label, value }: { label: string; value: ReactNode }): ReactElement {
  return (
    <>
      <dt className="truncate text-fg-muted">{label}</dt>
      <dd className="text-right tabular-nums">{value}</dd>
    </>
  );
}

export function CompanyName({
  name,
  company,
}: {
  name: string;
  company: ProductInsightsCompanyRef | null | undefined;
}): ReactElement {
  return company ? (
    <TextLink to={`/companies/${company.id}`} className="truncate font-medium">
      {company.name}
    </TextLink>
  ) : (
    <span className="truncate font-medium text-fg">{name}</span>
  );
}

/** Sub-header row inside a table body: "Institutions · 12". */
export function GroupHeaderRow({
  label,
  count,
  colSpan,
}: {
  label: string;
  count: number;
  colSpan: number;
}): ReactElement {
  return (
    <TR>
      <TD colSpan={colSpan} className="bg-surface-sunken py-1.5 text-xs font-semibold text-fg-secondary">
        {label}
        <span className="ml-1.5 font-normal text-fg-muted tabular-nums">{formatCount(count)}</span>
      </TD>
    </TR>
  );
}

export function GroupHeading({ label, count }: { label: string; count: number }): ReactElement {
  return (
    <h3 className="flex items-baseline gap-1.5 px-1 text-xs font-semibold text-fg-secondary">
      {label}
      <span className="font-normal text-fg-muted tabular-nums">{formatCount(count)}</span>
    </h3>
  );
}

/** The participating schools of one event, split Institutions / Partners. */
export function EventSchoolsPanel({ event }: { event: ProductInsightsEvent }): ReactElement {
  const groups = splitIntoGroups(rankedSchools(event), schoolGroup);
  const reachLabel = event.schools.some((school) => school.sharedReach) ? 'Shared reach' : 'Reach';

  if (groups.length === 0) {
    return <p className="py-2 text-sm text-fg-muted">No participating schools recorded.</p>;
  }

  return (
    <div>
      {/* Desktop */}
      <div className="max-md:hidden">
        <TableWrap className="rounded-md border border-hairline bg-surface">
          <Table>
            <THead>
              <TR>
                <TH className="w-full min-w-56">School</TH>
                <TH numeric className="min-w-36">Booth connections</TH>
                <TH numeric className="min-w-44">Presentation attendees</TH>
                <TH numeric className="min-w-28">{reachLabel}</TH>
              </TR>
            </THead>
            <TBody>
              {groups.map((group) => (
                <GroupRows key={group.label} label={group.label} count={group.rows.length}>
                  {group.rows.map((school, index) => (
                    <TR key={`${school.upstreamCompanyId ?? 'x'}:${school.schoolId ?? 'x'}:${index}`}>
                      <TD className="max-w-md">
                        <CompanyName name={schoolDisplayName(school)} company={school.company} />
                      </TD>
                      <TD numeric className="tabular-nums">
                        {formatCount(school.boothConnections)}
                      </TD>
                      <TD numeric className="tabular-nums">
                        {formatCount(school.presentationAttendees)}
                      </TD>
                      <TD numeric className="tabular-nums">
                        {formatCount(school.registrationsReached)}
                      </TD>
                    </TR>
                  ))}
                </GroupRows>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </div>

      {/* Phone: a divided list, not cards inside the event's card. */}
      <div className="space-y-3 md:hidden">
        {groups.map((group) => (
          <section key={group.label} className="space-y-1">
            <GroupHeading label={group.label} count={group.rows.length} />
            <ul className="divide-y divide-hairline">
              {group.rows.map((school, index) => (
                <li
                  key={`${school.upstreamCompanyId ?? 'x'}:${school.schoolId ?? 'x'}:${index}`}
                  className="min-w-0 px-1 py-2"
                >
                  <div className="flex min-w-0">
                    <CompanyName name={schoolDisplayName(school)} company={school.company} />
                  </div>
                  <dl className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-xs">
                    <CardPair label="Booth connections" value={formatCount(school.boothConnections)} />
                    <CardPair
                      label="Presentation attendees"
                      value={formatCount(school.presentationAttendees)}
                    />
                    <CardPair label={reachLabel} value={formatCount(school.registrationsReached)} />
                  </dl>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
    </div>
  );
}

function GroupRows({
  label,
  count,
  children,
}: {
  label: string;
  count: number;
  children: ReactNode;
}): ReactElement {
  return (
    <>
      <GroupHeaderRow label={label} count={count} colSpan={4} />
      {children}
    </>
  );
}
